import { useState, useRef, useCallback, useEffect } from 'react';

import {
    connectToAgent,
    disconnectFromAgent,
    sendMessageToAgent,
    isConnectedToAgent,
    deleteConversationFromAgent,
    createNewAgentConversation,
} from '@/shared/services/agentChatService';
import { useChatPageState } from '@/shared/contexts/ChatPageContext';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';

const WIDGET_PERSONA = 'rile';

// Build the greeting per-ecosystem so the widget intro reflects the active
// tenant ("Colorado STEM Ecosystem" for COSTEM, the tenant name otherwise,
// or a generic fallback in platform mode).
const buildWidgetGreeting = (ecosystemName) => ({
    id: 'widget-greeting',
    text: `Hi! I'm Aspen, here to help you navigate ${ecosystemName || 'this ecosystem'}. Ask me about organizations, mentors, or opportunities.`,
    sender: 'bot',
    timestamp: new Date(),
});

/**
 * Custom hook managing the floating chat widget lifecycle.
 * - Lazy connection: connects only when widget opens
 * - Conversation persists across page navigation
 * - Cleans up on explicit close (disconnect) unless promoted to assistant
 */
export default function useWidgetChat() {
    const { ecosystem } = useEcosystem();
    const ecosystemName = ecosystem?.name;
    const [messages, setMessages] = useState(() => [
        buildWidgetGreeting(ecosystemName),
    ]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [connected, setConnected] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [threadId, setThreadId] = useState(null);

    const messagesEndRef = useRef(null);
    const promotedRef = useRef(false);
    const { pageState } = useChatPageState();

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const connect = useCallback(async () => {
        if (isConnectedToAgent() || connecting) return;
        setConnecting(true);
        setError(null);

        try {
            await connectToAgent(null, WIDGET_PERSONA);
            // Create a proper {email}_{uuid} conversation so it's trackable
            const result = await createNewAgentConversation();
            setThreadId(result.threadId);
            setConnected(true);
        } catch {
            setError('Unable to connect. Please try again.');
            setConnected(false);
        } finally {
            setConnecting(false);
        }
    }, [connecting]);

    const disconnect = useCallback(() => {
        disconnectFromAgent();
        setConnected(false);

        // Clean up orphaned widget conversation unless promoted to assistant
        if (threadId && !promotedRef.current) {
            deleteConversationFromAgent(threadId).catch(() => {});
        }
        setThreadId(null);
        promotedRef.current = false;
    }, [threadId]);

    const send = useCallback(
        async (text) => {
            if (!text.trim() || loading) return;

            const userMessage = {
                id: `user-${Date.now()}`,
                text,
                sender: 'user',
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, userMessage]);
            setLoading(true);

            try {
                // Connect if not already connected
                if (!isConnectedToAgent()) {
                    await connectToAgent(null, WIDGET_PERSONA);
                    // Create a proper {email}_{uuid} conversation so it's trackable
                    const result = await createNewAgentConversation();
                    setThreadId(result.threadId);
                    setConnected(true);
                }

                const response = await sendMessageToAgent(text, {
                    page_context: pageState,
                });
                const botMessage = {
                    id: response.id || `bot-${Date.now()}`,
                    text: response.text,
                    sender: 'bot',
                    timestamp: response.timestamp || new Date(),
                };
                setMessages((prev) => [...prev, botMessage]);
            } catch (err) {
                const errorMessage = {
                    id: `error-${Date.now()}`,
                    text: `Sorry, I encountered an error: ${err.message || 'Please try again.'}`,
                    sender: 'bot',
                    timestamp: new Date(),
                    isError: true,
                };
                setMessages((prev) => [...prev, errorMessage]);
            } finally {
                setLoading(false);
            }
        },
        [loading, pageState]
    );

    const clearHistory = useCallback(() => {
        setMessages([buildWidgetGreeting(ecosystemName)]);
    }, [ecosystemName]);

    // Mark conversation as promoted so it isn't deleted on disconnect
    const promote = useCallback(() => {
        promotedRef.current = true;
    }, []);

    return {
        messages,
        loading,
        error,
        connected,
        connecting,
        messagesEndRef,
        threadId,
        connect,
        disconnect,
        send,
        clearHistory,
        promote,
    };
}
